import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Grow from '@base/Grow/Grow';
import P2 from '@base/P2/P2';
import H6 from '@base/H6/H6';
import Tags from '@base/Tags/Tags';
import Link from '@base/Link/Link';
import { getFullDate, isSameDay, getTime } from '@utils/dates';
import { getLink, getLinkPath } from '@models/Course';
import { isLegendEducator } from '@models/User';
import { Spacings } from '@styles/index';
import { useIsMobile } from '@stores/AppCommonData';
import getTopicGroupAssetObj from '@cont/Syllabus/constants/getTopicGroupAssetObj';
import { getIsGTPGoal } from '@api/selectors/constants';
import useConstants from '@api/hooks/useConstants';
import { getAuthorFullName } from '@api/processors/search';
import { pluralize } from '@utils/textHelper';
import { getCourseInfo, getIsOnlyRecordedClassesPresent } from './util';

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  background-color: var(--color-base-1);
  text-align: left;
  padding: ${Spacings.SPACING_4B}
    ${({ orientation }) =>
      orientation === 'horizontal' ? Spacings.SPACING_6B : Spacings.SPACING_4B};
`;

const TopicGroupTag = styled(Tags)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  padding: 0;
  width: 100%;
  max-width: none;
  color: ${({ color }) => color};
  @media only screen and (max-width: 47.95em) {
    font-size: 10px;
  }
`;

const BreadCrumbs = styled.div`
  padding-bottom: ${Spacings.SPACING_2B};
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-column-gap: ${Spacings.SPACING_2B};
  align-items: center;
  @media only screen and (max-width: 47.95em) {
    padding-bottom: 4px;
  }
`;

const CourseTitle = styled(H6)`
  padding-bottom: ${Spacings.SPACING_1B};
  color: var(--color-text-primary);
  @media only screen and (max-width: 47.95em) {
    padding-bottom: 0;
    font-size: 16px;
  }
`;

const CourseInfo = styled(P2)`
  padding-top: ${Spacings.SPACING_1B};
  padding-bottom: ${Spacings.SPACING_1B};
  @media only screen and (max-width: 47.95em) {
    padding-bottom: 0;
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const EducatorName = styled(P2)`
  padding-top: ${Spacings.SPACING_2B};
  color: var(--color-text-primary);
`;

export const getLiveAtTIme = (liveAtString) => {
  return liveAtString ? getTime(liveAtString) : '';
};

const getAuthorInfo = (course = {}) => {
  const { author, authors } = course;
  let authorID = authors?.[0]?.user?.relative_link || author?.relativeLink;
  if (!authorID && (authors?.[0]?.username || author?.username))
    authorID = `/@${authors?.[0]?.username || author?.username}`;
  const authorName = authors?.[0]?.name || getAuthorFullName(author);
  return { authorID, authorName };
};

const PlusCourseCardDetails = ({
  orientation,
  course,
  showScheduleInfo,
  cardType,
  topic,
  handleAnalytics,
  disabledTopicGroup,
  hideLessonCount
}) => {
  const isMobile = useIsMobile();
  const isGTP = useConstants(getIsGTPGoal(course.goal?.uid));
  if (!course) return null;
  const {
    language,
    name,
    topicGroups,
    topic: topicName,
    showAsLesson,
    nextTrack,
    programme,
    isSpecial
  } = course;

  const { authorID, authorName } = getAuthorInfo(course);
  const isOnlyRecordedClassesPresent =
    getIsOnlyRecordedClassesPresent(programme) ||
    getIsOnlyRecordedClassesPresent(course);
  const onlyShowLessonsCount = isOnlyRecordedClassesPresent && !isSpecial;

  const courseInfo = getCourseInfo(hideLessonCount, course);

  const getPreviouslyEnrolledCourseInfo = () => {
    const { isOngoing, isCompleted, isUpcoming, isLive, itemCount } = course;
    if (onlyShowLessonsCount) return `${pluralize(itemCount, 'session')}`;

    const liveAtString =
      nextTrack.liveAtString || nextTrack?.properties?.liveAt;
    const liveLessonStartTime = new Date(liveAtString);
    if (isLive) {
      return `Started at ${getLiveAtTIme(liveAtString)}`;
    }
    if (isOngoing) {
      const isToday = isSameDay(liveLessonStartTime, new Date());
      if (isToday) return `Today, ${getLiveAtTIme(liveAtString)}`;
      return `${getFullDate(liveAtString)} ${getLiveAtTIme(liveAtString)}`;
    }
    if (isUpcoming) {
      const { startsAt } = course;
      return `Starts on ${getFullDate(startsAt)} `;
    }
    if (isCompleted) {
      const { endsAt } = course;
      return `Ended on ${getFullDate(endsAt)} `;
    }
  };

  const userIsLegendEducator =
    course.authors && isLegendEducator(course.authors[0]);
  let topicData = {
    uid: topicGroups?.[0]?.uid,
    name: topicGroups?.[0]?.name || topicGroups?.[0]?.title || topicName || ''
  };

  const displayName = showAsLesson ? nextTrack.name : name;

  if (topic && topic.uid) {
    topicData = {
      uid: topic.uid,
      name: topic.name || topicName
    };
  }
  const { color } = getTopicGroupAssetObj(topicData.name);

  const onTopicClick = (e) => {
    if (isMobile) {
      e.preventDefault();
    }
  };

  const handleCourseClick = () => {
    handleAnalytics();
  };
  return (
    <Wrapper orientation={orientation}>
      <div>
        {!userIsLegendEducator ? (
          <BreadCrumbs>
            {language && language !== '' ? (
              <Tags type="hollow">{language}</Tags>
            ) : null}
            {topicData?.uid || topicData?.name ? (
              <>
                {disabledTopicGroup ? (
                  <TopicGroupTag type="transparent" color={color}>
                    {topicData.name}
                  </TopicGroupTag>
                ) : (
                  <Link
                    href="/goal/[goalSlug]/[goalUID]/topic/[topicUid]"
                    as={`/goal/${course.goal?.goalSlug || course.goal?.slug}/${
                      course.goal?.uid
                    }/topic/${topicData.uid}`}
                    anchorStyle="display: inline-block;min-width: 0;line-height: 1;"
                    onClick={onTopicClick}
                  >
                    <TopicGroupTag type="transparent" color={color}>
                      {topicData.name}
                    </TopicGroupTag>
                  </Link>
                )}
              </>
            ) : null}
          </BreadCrumbs>
        ) : null}
        <Link
          href={getLinkPath(course)}
          as={getLink(course)}
          onClick={handleCourseClick}
        >
          <CourseTitle
            lineClamp={2}
            as="h4"
            data-testid="course-tile-plus-card"
          >
            {displayName}
          </CourseTitle>
        </Link>
        {showScheduleInfo && (
          <>
            {courseInfo ? (
              <CourseInfo
                color="var(--color-text-secondary)"
                data-testid="course-info"
              >
                {cardType === 'all_cards' && courseInfo}
                {cardType === 'previouslyEnrolled' &&
                  getPreviouslyEnrolledCourseInfo()}
              </CourseInfo>
            ) : null}
          </>
        )}
      </div>
      <Grow />
      {!isGTP && authorID ? (
        <Link href="/[authorID]" as={authorID}>
          <EducatorName as="h4" data-testid="educator-name">
            {authorName}
          </EducatorName>
        </Link>
      ) : (
        <EducatorName as="h4">{authorName}</EducatorName>
      )}
    </Wrapper>
  );
};

export default PlusCourseCardDetails;

PlusCourseCardDetails.propTypes = {
  orientation: PropTypes.string.isRequired,
  course: PropTypes.objectOf(PropTypes.unknown).isRequired,
  showScheduleInfo: PropTypes.bool,
  cardType: PropTypes.string,
  topic: PropTypes.objectOf(PropTypes.unknown),
  handleAnalytics: PropTypes.func,
  disabledTopicGroup: PropTypes.bool,
  hideLessonCount: PropTypes.bool
};

PlusCourseCardDetails.defaultProps = {
  showScheduleInfo: true,
  cardType: 'all_cards',
  topic: {},
  handleAnalytics: () => {},
  disabledTopicGroup: false,
  hideLessonCount: false
};
